import { decodeToken } from "react-jwt";

const TOKEN_KEY = "info";
const API = () => process.env.REACT_APP_DEV_API;

// Fired when the server says the session is over (refresh cookie expired or revoked).
export const SESSION_EXPIRED_EVENT = "osi:session-expired";

export type RefreshResult =
  | { status: "ok"; token: string }
  | { status: "expired" } // the server rejected the refresh cookie
  | { status: "unavailable" }; // network error or server problem: keep what we have

let inFlight: Promise<RefreshResult> | null = null;

const sessionHeaders = { "X-OSI-Session": "refresh" };

/**
 * Exchanges the httpOnly refresh cookie for a new access token. Concurrent callers
 * share one request, so parallel API calls never rotate the cookie twice.
 */
export const refreshAccessToken = (): Promise<RefreshResult> => {
  if (inFlight) return inFlight;

  inFlight = (async (): Promise<RefreshResult> => {
    try {
      const response = await fetch(`${API()}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: sessionHeaders,
      });
      if (response.status === 401 || response.status === 404) {
        return { status: "expired" };
      }
      if (!response.ok) return { status: "unavailable" };
      const body = await response.json();
      const token = body?.data?.token;
      if (!token) return { status: "unavailable" };
      localStorage.setItem(TOKEN_KEY, token);
      return { status: "ok", token };
    } catch {
      return { status: "unavailable" };
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
};

export const signOutOnServer = async (): Promise<void> => {
  try {
    await fetch(`${API()}/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: sessionHeaders,
    });
  } catch {
    // The local sign-out still happens; the cookie lapses on its own.
  }
};

const expiryMs = (token: string): number | null => {
  const claims = decodeToken<{ exp?: number }>(token);
  return claims?.exp ? claims.exp * 1000 : null;
};

const announceExpired = () => window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));

/**
 * Returns a token that stays valid for at least `minValidMs`, refreshing when needed.
 * Resolves to null if there is no session or it has ended.
 */
export const ensureFreshToken = async (minValidMs = 60_000): Promise<string | null> => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;

  const exp = expiryMs(token);
  if (exp === null || exp - Date.now() > minValidMs) return token;

  const result = await refreshAccessToken();
  if (result.status === "ok") return result.token;
  if (result.status === "expired") {
    announceExpired();
    return null;
  }
  return token;
};

/**
 * fetch with the bearer token attached. Refreshes shortly before expiry, and retries
 * once on 401 with a freshly refreshed token (a 401 can also be a role rejection, in
 * which case the retry returns the same 401 and the session is left alone).
 */
export const authorizedFetch = async (
  input: RequestInfo | URL,
  init: RequestInit = {},
  { refresh = true }: { refresh?: boolean } = {},
): Promise<Response> => {
  const send = (token: string | null) =>
    fetch(input, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

  if (!refresh) return send(localStorage.getItem(TOKEN_KEY));

  const token = await ensureFreshToken();
  const response = await send(token ?? localStorage.getItem(TOKEN_KEY));
  if (response.status !== 401 || !token) return response;

  const retry = await refreshAccessToken();
  return retry.status === "ok" ? send(retry.token) : response;
};
